import { useMemo } from 'react';
import { Typography, Tooltip } from 'antd';
import { SmartDataTableProps } from '@src/components/SmartDataTable/types';
import { usePaymentModeRender } from './usePaymentModeRender';
import { useUserTimestampRender } from '@src/hooks';
import dayjs from 'dayjs';

const { Text } = Typography;

export const useTransactionsColumns = (): SmartDataTableProps['columns'] => {
  const renderPaymentMode = usePaymentModeRender();
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
  });

  return useMemo(
    () => [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        sorter: true,
        minWidth: 80,
        ellipsis: true,
        render: (value: any) => (
          <Text
            style={{
              fontSize: '12px',
              color: '#8c8c8c',
              backgroundColor: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            #{value}
          </Text>
        ),
      },
      {
        key: 'invoice_id',
        title: 'Invoice',
        dataIndex: 'invoice_id',
        sorter: true,
        minWidth: 100,
        render: (value: any, record: any) => {
          // Create tooltip content for invoice details
          const getTooltipContent = () => {
            if (record.invoice_details) {
              const invoice = record.invoice_details;
              const patient = record.patient;
              const doctor = invoice.doctor;
              const therapist = invoice.therapist;
              const counsellor = invoice.counsellor;
              const items = invoice.clinic_invoice_items || [];

              return (
                <div style={{ maxWidth: '450px' }}>
                  <div
                    style={{
                      fontWeight: 'bold',
                      marginBottom: '8px',
                      fontSize: '14px',
                    }}
                  >
                    Clinic Invoice Details
                  </div>

                  {/* Patient Information */}
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ marginBottom: '2px', fontSize: '12px' }}>
                      <strong>Patient Name:</strong> {patient?.first_name}{' '}
                      {patient?.last_name}
                    </div>
                    <div style={{ marginBottom: '2px', fontSize: '12px' }}>
                      <strong>Patient Mobile No:</strong>{' '}
                      {patient?.mobile_no || 'N/A'}
                    </div>
                  </div>

                  {/* Financial Information */}
                  <div style={{ marginBottom: '8px' }}>
                    <div
                      style={{
                        fontWeight: 'bold',
                        marginBottom: '4px',
                        color: '#fa8c16',
                      }}
                    >
                      Financial Details
                    </div>
                    <div style={{ marginBottom: '2px', fontSize: '12px' }}>
                      <strong>Total Amount:</strong> ₹
                      {invoice.total_amount || '0.00'}
                    </div>
                    <div style={{ marginBottom: '2px', fontSize: '12px' }}>
                      <strong>Final Amount:</strong> ₹
                      {invoice.final_amount || '0.00'}
                    </div>
                    <div style={{ marginBottom: '2px', fontSize: '12px' }}>
                      <strong>Due Amount:</strong> ₹
                      {invoice.due_amount || '0.00'}
                    </div>
                    <div style={{ marginBottom: '2px', fontSize: '12px' }}>
                      <strong>Status:</strong> {invoice.invoice_status || 'N/A'}
                    </div>
                  </div>

                  {/* Invoice Items */}
                  {items.length > 0 && (
                    <div>
                      <div
                        style={{
                          fontWeight: 'bold',
                          marginBottom: '4px',
                          color: '#722ed1',
                        }}
                      >
                        Services/Treatments
                      </div>
                      {items.slice(0, 3).map((item: any, index: number) => (
                        <div
                          key={index}
                          style={{
                            fontSize: '11px',
                            marginBottom: '3px',
                            padding: '2px 4px',

                            borderRadius: '3px',
                          }}
                        >
                          <div style={{ fontWeight: 'bold' }}>
                            • {item.item_name}
                          </div>
                          <div style={{ color: '#666', marginLeft: '8px' }}>
                            <div>
                              Price: ₹{item.unit_price} | Qty: {item.quantity} |
                              Sessions: {item.total_sessions}
                            </div>
                            <div>
                              Discount: {item.discount}% | GST: {item.gst_rate}%
                              | Final: ₹{item.final_price}
                            </div>
                          </div>
                        </div>
                      ))}
                      {items.length > 3 && (
                        <div
                          style={{
                            fontSize: '12px',

                            fontStyle: 'italic',
                          }}
                        >
                          +{items.length - 3} more services
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            }

            return 'No invoice details available';
          };

          return (
            <Tooltip
              title={getTooltipContent()}
              placement="topLeft"
              overlayStyle={{ maxWidth: '450px' }}
            >
              <Text
                style={{ fontWeight: 500, color: '#262626', cursor: 'pointer' }}
              >
                {value ? `INV-${value}` : 'N/A'}
              </Text>
            </Tooltip>
          );
        },
      },
      {
        key: 'amount',
        title: 'Amount',
        dataIndex: 'amount',
        sorter: true,
        minWidth: 120,
        render: (amount: string) => (
          <Text style={{ fontWeight: 500, color: '#52c41a' }}>
            ₹
            {amount
              ? Number(amount).toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                })
              : '0.00'}
          </Text>
        ),
      },
      {
        key: 'payment_mode',
        title: 'Payment Mode',
        dataIndex: 'payment_mode',
        sorter: true,
        minWidth: 130,
        render: renderPaymentMode,
      },
      {
        key: 'user_timestamp',
        title: 'User & Timestamp',
        dataIndex: 'created_at',
        sorter: false,
        minWidth: 200,
        render: (value: any, record: any) => userTimestampRender(record),
      },
    ],
    [renderPaymentMode, userTimestampRender]
  );
};
